import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Typography,
  Card,
  Tabs,
  Modal,
  message,
  Statistic,
  Tag,
  Tooltip,
  Spin,
} from "antd";
import { trackFeatureUsage } from "../lib/posthog";
import styled from "@emotion/styled";
import { AdminNavBar } from "../components/AdminNavBar";
import { supabase } from "../lib/supabase";
import { SupabaseClient } from "@supabase/supabase-js";
import { useSearchParams } from "react-router-dom";

// Type cast supabase to avoid type errors
const typedSupabase = supabase as unknown as SupabaseClient;

// Import types
import { Booking, bookingStatusLabels } from "../types/booking";
import { hasWms, type Company as CompanyWithWms } from "../types/company";

// Import components
import BookingsList from "../components/BookingsAdmin/BookingsList";
import BookingDetails from "../components/BookingsAdmin/BookingDetails";

// Import services
import {
  fetchBookingStatusCounts,
  fetchTotalBookingsCount,
  fetchBookingById,
} from "../services/bookingService";

// Import utils
import { generateAndDownloadPackingSlip } from "../services/packingSlipService";
import { validStatuses, statusColors } from "../utils/bookingStatus";
import {
  useTabNotifications,
  playNotificationSound,
  updateTabTitle,
  showBrowserNotification,
} from "../utils/notificationUtils";

// Import custom hooks
import { useBookingUpdates } from "../hooks/useBookingUpdates";

const { Title, Link } = Typography;
const { TabPane } = Tabs;

// Define styled components
const Container = styled.div`
  padding: 24px;
`;

const StatusMetricCard = styled(Card)`
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .ant-statistic-title {
    font-size: 12px;
    margin-bottom: 0;
  }

  .ant-statistic-content {
    font-size: 18px;
  }
`;

const StatusMetricsWrapper = styled.div`
  margin-bottom: 16px;
`;

const StatusMetricsToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  min-height: 20px;
  margin-bottom: 4px;
`;

const StatusMetricsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const BookingsAdmin: React.FC = () => {
  // Navigation and URL params
  const [searchParams, setSearchParams] = useSearchParams();

  // Get URL query parameters
  const bookingIdParam = searchParams.get("booking_id");
  const tabParam = searchParams.get("tab");

  // State for bookings
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [viewDetailsModalVisible, setViewDetailsModalVisible] = useState(false);
  const [defaultTabForModal, setDefaultTabForModal] = useState<string>("1");

  // State for triggering list refresh on real-time updates
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // State for stats
  const [statsLoading, setStatsLoading] = useState(true);
  const [totalBookingsCount, setTotalBookingsCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<
    { status: string; count: number }[]
  >([]);

  // Reference to track previous pending count for sound notifications
  const prevPendingCountRef = useRef<number>(0);
  const hasLoadedStatsOnceRef = useRef(false);

  // State for active tab - initialize from URL parameter
  const [activeTab, setActiveTab] = useState(tabParam || "all");

  // State for selected status filter (synced from URL; metric clicks set it immediately)
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<
    string | null
  >(() => searchParams.get("status"));

  const statusFromUrl = searchParams.get("status");
  useEffect(() => {
    setSelectedStatusFilter(statusFromUrl);
  }, [statusFromUrl]);

  // Add state for resetting filters
  const [resetFilters, setResetFilters] = useState<number | null>(null);

  // Add state to track if there are unsaved changes in the booking details
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showAllStatusTabs, setShowAllStatusTabs] = useState(false);

  // Calculate pending approvals count
  const pendingApprovalsCount =
    statusCounts.find((s) => s.status === "submitted")?.count || 0;

  // IIFE to force title update on component creation for the badge
  // This runs immediately when the component is defined
  (() => {
    // Get stored count if it exists
    const storedCount = localStorage.getItem("pendingApprovalCount");
    const count = storedCount ? parseInt(storedCount) : 0;

    if (count > 0) {
      // Set document title directly
      document.title = `(${count}) Bookings Admin`;

      // Try window method too
      if (window.updateTitleWithCount) {
        window.updateTitleWithCount(count);
      }
    }
  })();

  useEffect(() => {
    // Store the count in localStorage for persistence
    localStorage.setItem(
      "pendingApprovalCount",
      pendingApprovalsCount.toString(),
    );

    // Update the title in multiple ways for maximum compatibility
    // 1. Direct document.title update
    if (pendingApprovalsCount > 0) {
      document.title = `(${pendingApprovalsCount}) Bookings Admin`;
    } else {
      document.title = "Bookings Admin";
    }

    // 2. Use the global window method
    if (window.updateTitleWithCount) {
      try {
        window.updateTitleWithCount(pendingApprovalsCount);
      } catch (e) {
        console.error("Error calling window.updateTitleWithCount:", e);
      }
    }

    // 3. Use our utility function as fallback
    updateTabTitle(pendingApprovalsCount);
  }, [pendingApprovalsCount]);

  // Use the hook for tab notifications with badge and sound
  useTabNotifications(pendingApprovalsCount, true, prevPendingCountRef);

  // Load stats - defined early so it can be used in the dependency array
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      // Fetch status counts
      const statusResult = await fetchBookingStatusCounts();

      if (!statusResult.error) {
        // Update the state with the fetched status counts
        setStatusCounts(statusResult.data);

        // Check if pending count increased
        const newPendingCount =
          statusResult.data.find((s) => s.status === "submitted")?.count || 0;
        const prevPendingCount = prevPendingCountRef.current;

        // Update title immediately for faster badge display
        if (newPendingCount > 0) {
          document.title = `(${newPendingCount}) Bookings Admin`;

          // Also store in localStorage
          localStorage.setItem(
            "pendingApprovalCount",
            newPendingCount.toString(),
          );

          // Use window method if available
          if (window.updateTitleWithCount) {
            window.updateTitleWithCount(newPendingCount);
          }
        }

        // Play sound and show notification ONLY if pending count actually increased
        if (newPendingCount > prevPendingCount) {
          playNotificationSound();

          // Also show a browser notification for new pending approvals
          showBrowserNotification(
            `You have ${newPendingCount} pending booking${
              newPendingCount !== 1 ? "s" : ""
            } that require approval`,
          );
        }

        // Update reference for next comparison
        prevPendingCountRef.current = newPendingCount;
      } else {
        console.error("Error fetching status counts:", statusResult.error);
      }

      // Fetch total count
      const countResult = await fetchTotalBookingsCount();

      if (!countResult.error) {
        setTotalBookingsCount(countResult.count);
      } else {
        console.error("Error fetching total count:", countResult.error);
      }
    } catch (error) {
      console.error("Error loading booking stats:", error);
      message.error("Failed to load booking statistics");
    } finally {
      setStatsLoading(false);
      hasLoadedStatsOnceRef.current = true;
    }
  }, []);

  // Force refresh of bookings list - define this early so it can be referenced
  const forceBookingsRefresh = useCallback(() => {
    // Generate a unique timestamp that will definitely be different
    const refreshTimestamp = new Date();
    // Use a timeout to ensure state updates don't conflict
    setTimeout(() => {
      setLastUpdate(refreshTimestamp);
    }, 100);
  }, []);

  // Handle booking update from real-time subscription
  const handleBookingUpdate = useCallback(
    (booking: Booking) => {
      // @ts-expect-error - is_refresh_only is a custom property we added
      const isRefreshOnly = booking.is_refresh_only === true;

      // Check if this is a special polling refresh (shouldn't affect counts)
      const isPollingRefresh = booking.status === "polling_refresh";

      // For polling refreshes, we only need to check for new pending bookings
      // We can optimize by doing a focused check for pending approvals only
      if (isPollingRefresh) {
        // Only refresh the pending approvals count, not all statuses
        // This is a more targeted approach that avoids unnecessary database queries
        const checkPendingApprovalsOnly = async () => {
          try {
            // Only fetch the count of pending approvals
            const { count, error } = await typedSupabase
              .from("bookings")
              .select("id", { count: "exact", head: true })
              .eq("status", "submitted");

            if (error) {
              console.error("Error checking pending approvals count:", error);
              return;
            }

            // Update the count if it's different from what we have
            const currentPendingCount =
              statusCounts.find((s) => s.status === "submitted")?.count || 0;

            if (count !== currentPendingCount) {
              // Only update the submitted count, not all statuses
              const updatedStatusCounts = [...statusCounts];
              const submittedIndex = updatedStatusCounts.findIndex(
                (s) => s.status === "submitted",
              );

              if (submittedIndex >= 0) {
                // Update only the submitted count
                updatedStatusCounts[submittedIndex] = {
                  status: "submitted",
                  count: count || 0,
                };
                setStatusCounts(updatedStatusCounts);
              }

              // Force a refresh of the bookings list if we're on the pending tab
              if (activeTab === "pending") {
                forceBookingsRefresh();
              }
            }
          } catch (err) {
            console.error("Error in focused pending approvals check:", err);
          }
        };

        checkPendingApprovalsOnly();
        return;
      }

      // Check if this is a newly submitted booking
      const isNewPendingApproval = booking.status === "submitted";

      // For actual booking updates, we should still refresh all status counts
      // since the status of a booking might have changed
      loadStats();

      // Special approach for refreshing the table with a new Date object
      // This forces React to recognize the object as changed
      forceBookingsRefresh();

      // If the updated booking is the selected one, refresh its details
      if (selectedBooking && selectedBooking.id === booking.id) {
        // Directly fetch updated booking details instead of calling handleViewBookingDetails
        // This avoids the circular dependency
        fetchBookingById(booking.id)
          .then((result) => {
            if (!result.error && result.data) {
              setSelectedBooking(result.data);
            }
          })
          .catch((err) => {
            console.error("Error refreshing booking details:", err);
          });
      }

      // Only play notification sound for pending approvals if this is a real booking update
      // and not just a refresh from polling
      if (isNewPendingApproval && !isRefreshOnly) {
        playNotificationSound();

        // Only show browser notification for real bookings, not our sentinel value or refresh polls
        if (booking.id > 0) {
          showBrowserNotification("New booking requires your approval");
        }
      }
    },
    [selectedBooking, loadStats, forceBookingsRefresh, statusCounts, activeTab],
  );

  // Subscribe to real-time updates (now just polling)
  const { manualPoll, cancelRequests } = useBookingUpdates({
    onUpdate: handleBookingUpdate,
    activeTab: activeTab,
    pollOnlyWhenTabIs: "pending",
    // Disable realtime and force fallback mode
    enableRealtime: false,
    enableFallback: true,
    fallbackInterval: 30000, // Poll every 30 seconds (reduced from 2 minutes for easier testing)
  });

  // View booking details - defined early for dependency array
  const handleViewBookingDetails = useCallback(
    async (booking: Booking, defaultTab?: string) => {
      try {
        // Cancel any pending polling requests when opening details
        if (activeTab === "pending") {
          cancelRequests();
        }

        // Set default tab if provided
        if (defaultTab) {
          setDefaultTabForModal(defaultTab);
        } else {
          // Use URL param or default to "1"
          setDefaultTabForModal(
            tabParam === "products" ? "3" : tabParam === "customs" ? "9" : "1",
          );
        }

        // Always fetch full booking details to ensure we have fresh data
        const result = await fetchBookingById(booking.id);
        if (result.error) {
          message.error("Failed to load complete booking details");
          return;
        }
        setSelectedBooking(result.data);

        setViewDetailsModalVisible(true);
      } catch (error) {
        console.error("Error viewing booking details:", error);
        message.error("Failed to load booking details");
      }
    },
    [activeTab, cancelRequests, tabParam],
  );

  // Effect to track usage and load stats
  useEffect(() => {
    // Track page view
    trackFeatureUsage("bookings_admin");

    // Fetch status counts and total count
    loadStats();

    // Force a second load of stats after a delay to ensure counts are updated
    setTimeout(() => {
      loadStats();
    }, 2000);

    // Force update the title immediately after component mounts
    // This ensures the badge appears on initial load
    setTimeout(() => {
      const pendingCount =
        statusCounts.find((s) => s.status === "submitted")?.count || 0;
      updateTabTitle(pendingCount);

      // Also set the document.title directly for extreme reliability
      if (pendingCount > 0) {
        document.title = `(${pendingCount}) Bookings Admin`;
      }
    }, 1000); // Small delay to ensure counts are loaded
  }, [loadStats]);

  // Sync activeTab with URL parameter
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    const newTab = urlTab || "all";
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);

  // Handle tab change
  const handleTabChange = (key: string) => {
    // If switching away from pending tab, cancel any in-flight requests
    if (activeTab === "pending" && key !== "pending") {
      cancelRequests();
    }

    setActiveTab(key);

    // Update URL parameter
    const newParams = new URLSearchParams(searchParams);
    if (key === "all") {
      newParams.delete("tab");
    } else {
      newParams.set("tab", key);
    }
    setSearchParams(newParams, { replace: true });

    // Reset the status filter when changing tabs
    setSelectedStatusFilter(null);

    // If switching to pending tab, do an immediate poll
    if (key === "pending") {
      manualPoll();
      loadStats(); // Also refresh stats
      // Also force a list refresh to ensure we have the latest data
      forceBookingsRefresh();
    }
  };

  // Handle successful details update
  const handleUpdateSuccess = () => {
    // Refresh the booking list by updating the lastUpdate timestamp
    forceBookingsRefresh();

    // Refresh stats
    loadStats();
  };

  // Handle packing list generation and download
  const handleDownloadPackingSlip = async (booking: Booking) => {
    try {
      await generateAndDownloadPackingSlip(booking);
    } catch (error) {
      console.error("Error generating packing list:", error);
      message.error("Failed to generate packing list");
    }
  };

  // Handle status metric click
  const handleStatusMetricClick = (status: string) => {
    trackFeatureUsage("booking_status_metric_clicked", { status });
    setSelectedStatusFilter(status);

    // If we're on the all tab, we don't need to change it
    if (activeTab !== "all") {
      setActiveTab("all");
    }

    // Set flag to reset all other filters
    setResetFilters(new Date().getTime());
  };

  // Format status for display
  const formatStatusLabel = (status: string) => {
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Handle booking details modal cancel with unsaved changes check
  const handleModalClose = useCallback(() => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: "Unsaved Changes",
        content:
          "You have unsaved changes. Are you sure you want to close this dialog?",
        okText: "Yes, Close",
        cancelText: "No, Stay",
        onOk: () => {
          // Clear modal and reset selected booking to prevent state persistence between bookings
          setViewDetailsModalVisible(false);
          setSelectedBooking(null);
          setDefaultTabForModal("1"); // Reset to default tab
          setHasUnsavedChanges(false);

          // Resume polling if we're on the pending tab
          if (activeTab === "pending") {
            manualPoll();
          }
        },
      });
    } else {
      // No unsaved changes, close directly
      setViewDetailsModalVisible(false);
      setSelectedBooking(null);
      setDefaultTabForModal("1"); // Reset to default tab

      // Resume polling if we're on the pending tab
      if (activeTab === "pending") {
        manualPoll();
      }
    }
  }, [activeTab, hasUnsavedChanges, manualPoll]);

  // Effect to load booking from URL parameter if present
  useEffect(() => {
    const loadBookingFromUrl = async () => {
      // Check if booking_id parameter exists
      if (bookingIdParam) {
        try {
          const bookingId = parseInt(bookingIdParam);
          if (!isNaN(bookingId)) {
            // Fetch booking details
            const result = await fetchBookingById(bookingId);
            if (!result.error && result.data) {
              // Set default tab from URL (e.g. tab=products → 3, tab=customs → 9)
              setDefaultTabForModal(
                tabParam === "products"
                  ? "3"
                  : tabParam === "customs"
                    ? "9"
                    : "1",
              );
              // Set selected booking and open modal
              setSelectedBooking(result.data);
              setViewDetailsModalVisible(true);
            } else {
              console.error(
                "Error loading booking from URL parameter:",
                result.error,
              );
              message.error(`Booking with ID ${bookingId} not found`);
            }
          }
        } catch (error) {
          console.error("Error loading booking from URL parameter:", error);
          message.error("Failed to load booking details");
        }
      }
    };

    loadBookingFromUrl();
  }, [bookingIdParam, tabParam]); // Run when booking_id or tab in URL changes

  const activeStatusHighlight = statusFromUrl ?? selectedStatusFilter ?? "all";

  const isInitialStatsLoad = statsLoading && !hasLoadedStatsOnceRef.current;

  const visibleStatuses =
    isInitialStatsLoad || showAllStatusTabs
      ? validStatuses
      : validStatuses.filter((status) => {
          const count =
            statusCounts.find((s) => s.status === status)?.count || 0;
          return count > 0;
        });

  const hasHiddenStatusTabs =
    !isInitialStatsLoad &&
    validStatuses.some(
      (status) =>
        (statusCounts.find((s) => s.status === status)?.count || 0) === 0,
    );

  const showPendingTab = isInitialStatsLoad || pendingApprovalsCount > 0;

  const effectiveActiveTab =
    !isInitialStatsLoad &&
    activeTab === "pending" &&
    pendingApprovalsCount === 0
      ? "all"
      : activeTab;

  // Redirect away from pending tab when it has no bookings
  useEffect(() => {
    if (
      !isInitialStatsLoad &&
      activeTab === "pending" &&
      pendingApprovalsCount === 0
    ) {
      setActiveTab("all");
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("tab");
      setSearchParams(newParams, { replace: true });
    }
  }, [
    isInitialStatsLoad,
    activeTab,
    pendingApprovalsCount,
    searchParams,
    setSearchParams,
  ]);

  return (
    <Container>
      <AdminNavBar />

      <Title level={2}>
        Bookings Management
        {activeTab === "pending" ? (
          <Tag color="blue" style={{ marginLeft: 8, fontSize: 12 }}>
            Auto-refresh active (every 30s)
          </Tag>
        ) : (
          <Tooltip title="Switch to Pending Approval tab for auto-refresh">
            <Tag
              color="orange"
              style={{ marginLeft: 8, fontSize: 12, cursor: "pointer" }}
              onClick={() => {
                if (!showPendingTab) {
                  message.info("No bookings pending approval");
                  return;
                }
                setActiveTab("pending");
                message.info(
                  "Switched to Pending Approval tab for auto-refresh",
                );
              }}
            >
              Auto-refresh paused
            </Tag>
          </Tooltip>
        )}
        <Tag
          color="green"
          style={{ marginLeft: 8, cursor: "pointer" }}
          onClick={() => {
            // Test sound function
            playNotificationSound();
            message.success("Test notification sound played");
          }}
        >
          Test Sound
        </Tag>
      </Title>

      {/* Status Metrics */}
      <StatusMetricsWrapper>
        <StatusMetricsToolbar>
          {(hasHiddenStatusTabs || showAllStatusTabs) && (
            <Link
              style={{ fontSize: 12 }}
              onClick={() => {
                setShowAllStatusTabs((current) => !current);
                trackFeatureUsage("booking_status_tabs_show_all_toggled", {
                  show_all: !showAllStatusTabs,
                });
              }}
            >
              {showAllStatusTabs ? "Hide empty" : "Show all"}
            </Link>
          )}
        </StatusMetricsToolbar>
        <StatusMetricsContainer>
        {/* Total bookings metric */}
        <StatusMetricCard
          key="total"
          onClick={() => {
            trackFeatureUsage("booking_total_metric_clicked");
            setSelectedStatusFilter("all");
            if (activeTab !== "all") {
              setActiveTab("all");
            }
            // Set flag to reset all other filters
            setResetFilters(new Date().getTime());
          }}
          style={{
            borderTop: `2px solid #1890ff`,
            background:
              activeStatusHighlight === "all"
                ? `rgba(24, 144, 255, 0.1)`
                : "white",
          }}
          bodyStyle={{ padding: "8px 12px" }}
        >
          <Statistic
            title="Total"
            value={totalBookingsCount || 0}
            loading={isInitialStatsLoad}
            valueStyle={{
              color: activeStatusHighlight === "all" ? "#1890ff" : "inherit",
              fontWeight: activeStatusHighlight === "all" ? "bold" : "normal",
            }}
          />
        </StatusMetricCard>

        {visibleStatuses.map((status) => {
          // Find the count for this status
          const statusData = statusCounts.find((s) => s.status === status);
          // Use the count if found, otherwise 0
          const count = statusData?.count || 0;

          const color = statusColors[status as keyof typeof statusColors];

          return (
            <StatusMetricCard
              key={status}
              onClick={() => handleStatusMetricClick(status)}
              style={{
                borderTop: `2px solid ${color}`,
                background:
                  activeStatusHighlight === status ? `${color}10` : "white",
              }}
              bodyStyle={{ padding: "8px 12px" }}
            >
              <Statistic
                title={formatStatusLabel(status)}
                value={count}
                loading={isInitialStatsLoad}
                valueStyle={{
                  color: activeStatusHighlight === status ? color : "inherit",
                  fontWeight:
                    activeStatusHighlight === status ? "bold" : "normal",
                }}
              />
            </StatusMetricCard>
          );
        })}
        </StatusMetricsContainer>
      </StatusMetricsWrapper>

      <Tabs
        activeKey={effectiveActiveTab}
        onChange={handleTabChange}
        style={{ marginBottom: 16 }}
      >
        {showPendingTab && (
          <TabPane
            tab={
              <span>
                Pending Approval
                {isInitialStatsLoad ? (
                  <Spin size="small" style={{ marginLeft: 8 }} />
                ) : (
                  pendingApprovalsCount > 0 && (
                    <Tag
                      color="red"
                      style={{
                        marginLeft: 8,
                        borderRadius: "10px",
                        fontSize: "12px",
                        lineHeight: "16px",
                        padding: "0 6px",
                      }}
                    >
                      {pendingApprovalsCount}
                    </Tag>
                  )
                )}
              </span>
            }
            key="pending"
          >
            <BookingsList
              isPendingList
              onViewDetails={handleViewBookingDetails}
              onRefresh={loadStats}
              lastUpdate={lastUpdate}
              statusFilter={selectedStatusFilter ?? undefined}
              resetFilters={resetFilters}
            />
          </TabPane>
        )}
        <TabPane tab="All Bookings" key="all">
          <BookingsList
            onViewDetails={handleViewBookingDetails}
            onRefresh={loadStats}
            lastUpdate={lastUpdate}
            statusFilter={selectedStatusFilter ?? undefined}
            resetFilters={resetFilters}
          />
        </TabPane>
      </Tabs>

      {/* Booking Details Modal */}
      <Modal
        title={
          selectedBooking ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingRight: 24,
              }}
            >
              <span>
                {(() => {
                  const transportAbbreviation =
                    selectedBooking.transport_type_info
                      ? Array.isArray(selectedBooking.transport_type_info)
                        ? selectedBooking.transport_type_info[0]?.abbreviation ||
                          ""
                        : selectedBooking.transport_type_info?.abbreviation || ""
                      : "";

                  const companyName = selectedBooking.company_info
                    ? Array.isArray(selectedBooking.company_info)
                      ? selectedBooking.company_info[0]?.name
                      : selectedBooking.company_info?.name
                    : "Unknown Company";

                  const statusLabel = selectedBooking.status
                    ? bookingStatusLabels[
                        selectedBooking.status as keyof typeof bookingStatusLabels
                      ] || selectedBooking.status.replace(/_/g, " ")
                    : "Unknown Status";

                  return `Booking Details - EFF${selectedBooking.id}${transportAbbreviation} - ${companyName} - ${statusLabel}`;
                })()}
              </span>
              {(() => {
                const company = selectedBooking.company_info
                  ? Array.isArray(selectedBooking.company_info)
                    ? selectedBooking.company_info[0]
                    : selectedBooking.company_info
                  : null;
                const companyWithWms = company as CompanyWithWms | null | undefined;
                const activeWmsName = hasWms(companyWithWms)
                  ? companyWithWms?.wms_provider === "channeldock"
                    ? "ChannelDock"
                    : "Picqer"
                  : null;
                return activeWmsName ? (
                  <span style={{ fontSize: 12, color: "#666" }}>
                    {activeWmsName}
                  </span>
                ) : null;
              })()}
            </div>
          ) : (
            "Booking Details"
          )
        }
        open={viewDetailsModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={1200}
      >
        {selectedBooking && (
          <BookingDetails
            key={selectedBooking.id}
            booking={selectedBooking}
            onEdit={() => {}}
            onDownloadPackingSlip={handleDownloadPackingSlip}
            onRefresh={async () => {
              // Refresh stats
              loadStats();

              // Update list trigger using our new robust method
              forceBookingsRefresh();

              // Refetch the selected booking to get updated data
              if (selectedBooking) {
                try {
                  const result = await fetchBookingById(selectedBooking.id);
                  if (!result.error && result.data) {
                    setSelectedBooking(result.data);
                  }
                } catch (error) {
                  console.error("Error refreshing booking details:", error);
                  // Continue execution - we don't want to block other refresh operations
                }
              }

              // Signal success for any other components
              handleUpdateSuccess();

              // Reset unsaved changes flag after refresh
              setHasUnsavedChanges(false);
            }}
            onChangesMade={(hasChanges) => setHasUnsavedChanges(hasChanges)}
            defaultTab={defaultTabForModal}
          />
        )}
      </Modal>
    </Container>
  );
};

export default BookingsAdmin;
